<script lang="tsx">
  import {
    defineComponent, PropType, computed,
    onUnmounted,
    inject,
  } from 'vue'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import { isFunction } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import IconTooltip from '@xhs/yam-beer/cmp/overlayers/Tooltip/IconTooltip.vue'
  import { StepStatus } from '@xhs/yam-beer/cmp/constants/StepStatus'
  import { getCurrentStatus } from './utils'
  import BeerStepCircle from './StepCircle.vue'
  import { BeerStepItem } from './BeerSteps.d'

  import initStyle from './style'

  type fn = () => void
  interface BeerTooltipProps {
      icon: any
      content: string | number | fn
      contentBs: BeerStyleObj
  }

  initStyle()

  /**
   * 每个步骤图标组件，被 Steps 引用使用
   */
  export default defineComponent({
    name: 'Step',
    props: {
      title: {
        type: String,
        required: true,
      },
      /**
       * 提示内容，可以是 JSX 函数..
       */
      content: [String, Function],
      /**
       * @ignore
       */
      stepNo: Number,
      /**
       * 步骤状态
       *
       * 默认会根据 Steps 的状态计算，但是也可以从这里覆盖，如 failed
       *
       * @type {done|progress|failed|pending}
       */
      status: {
        type: String as PropType<StepStatus>,
        required: false,
      },
      /**
       * 标题边上的提示
       *
       * @see 例子：标题的提示 #标题的提示
       */
      tipsProps: {
        type: Object as PropType<Partial<BeerTooltipProps>>,
      },
      /**
       * 图标替换数字
       *
       * @see Icon列表 /beer-next/ui-blocks/common/icon-doc
       */
      icon: String,
      /**
       * @ignore
       * 容器 BeerStyle
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @ignore
       */
      isFromConfig: {
        type: Boolean,
        default: false,
      },
    },
    setup(props, { slots, attrs }) {
      const wrapperBs = computed(() => ({
        'cmp.steps.number': 'wrapper',
        ...props.bs,
      }))

      const circleBs: BeerStyleObj = {
        'cmp.steps.number': 'circle',
        'cmp.steps.number.circleStatus': true,
      }

      const toolTipBs: BeerStyleObj = { ml: 8 }

      const myGetter = {
        stepNo: props.stepNo ?? -1,
        status: computed(() => props.status),
      }
      const control = inject('step', {
        // eslint-disable-next-line
        addStep(item: BeerStepItem): number { return 0 },
        // eslint-disable-next-line
        removeStep(title: string) {},
        currentStep: { value: -1 },
        // eslint-disable-next-line
        progressDot: ({ status, stepNo }: { status?: string, stepNo?: number }) => null,
        // eslint-disable-next-line
        onClickStep: (setpNo: number) => {},
      })
      if (!props.isFromConfig) {
        myGetter.stepNo = control.addStep({
          title: props.title,
          status: props.status,
        })
        myGetter.status = computed(() => getCurrentStatus(
          props.status,
          myGetter.stepNo,
          control.currentStep.value,
        ))

        onUnmounted(() => {
          control.removeStep(props.title)
        })
      }
      return () => {
        const {
          title, content, icon, tipsProps,
        } = props
        const status = props.status || myGetter.status.value
        const { stepNo } = myGetter
        let child = content
        if (isFunction(child)) {
          child = child()
        }

        function renderIcon() {
          if (control.progressDot) {
            return control.progressDot({ status, stepNo })
          }
          return (
            <NewBsBox
              bs={circleBs}
              context={{ status }}
              class={control.onClickStep && 'click-able'}
              // @ts-ignore
              onClick={control.onClickStep ? () => {
                if (isFunction(control.onClickStep)) {
                  control.onClickStep(stepNo)
                }
              } : null }
            >
              <BeerStepCircle
                status={status}
                stepNo={stepNo}
                icon={icon}
                />
            </NewBsBox>
          )
        }

        return (
        <NewBsBox bs={wrapperBs.value} {...attrs}>
          { renderIcon() }
          { slots.title ? slots.title({ title, status }) : (
            <NewBsBox
              data-title
              context={{ status }}
              bs={{ 'cmp.steps.number': 'title' }}
            >
              {title}
              {tipsProps && (
                // @ts-ignore
                <IconTooltip
                  icon={{ bs: toolTipBs }}
                  {...tipsProps}
                />
              )}
            </NewBsBox>
          )}
          { slots.content ? slots.content({ status }) : (
            <NewBsBox
              data-content
              context={{ status }}
              bs={{ 'cmp.steps.number': 'content' }}
            >
              {child}
            </NewBsBox>
          )}
        </NewBsBox>
      )
      }
    },
  })
</script>
